import { styled, SxProp, View } from 'dripsy';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';

import { Icon } from '@/design-system/components/icons/Icon';
import { H6, TextBody, TextCaption, TextSmall } from '@/design-system/components/typography';
import { getEarnTenorTranslationKey } from '../../utils/getEarnTenorTranslationKey';
import { PopularOfferItemProps } from './PopularOfferItem';
import { formatAmountByDecimals } from '@/utils/numberFormatting';

const PressableView = styled(Pressable)();

interface PopularOfferNativeItemProps extends PopularOfferItemProps {}

export const PopularOfferItemNative: React.FC<PopularOfferNativeItemProps> = ({ offer, sx, onPress }) => {
  const { t } = useTranslation('earn');
  const tenorTranslationKey = getEarnTenorTranslationKey(offer.tenor);

  return (
    <PressableView sx={styles.container} onPress={onPress}>
      <View sx={styles.top}>
        <TenorTag tenor={t(tenorTranslationKey.key, tenorTranslationKey.options)} />
        <TextBody semiBold>{offer.title}</TextBody>
      </View>
      <View sx={styles.bottom}>
        <ApyTag apy={offer.estimatedApy} />
      </View>
    </PressableView>
  );
};

const TenorTag = ({ tenor }: { tenor: string }) => {
  return (
    <View sx={styles.tenorTag}>
      <Icon height={18} width={18} name="tenor" />
      <TextSmall semiBold sx={{ color: '$textThemeSecondary' }}>
        {tenor}
      </TextSmall>
    </View>
  );
};

const ApyTag = ({ apy }: { apy: number }) => {
  return (
    <View sx={styles.apyTag}>
      <TextCaption sx={{ color: '$textThemeSecondary' }}>APY</TextCaption>
      <H6 bold sx={{ color: '$textThemeSecondary' }}>
        {formatAmountByDecimals({ value: apy, decimals: 2, suffix: '%' })}
      </H6>
    </View>
  );
};

enum StyleName {
  container = 'container',
  top = 'top',
  tenorTag = 'tenorTag',
  apyTag = 'apyTag',
  bottom = 'bottom'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    width: '48%',
    maxWidth: '48%',
    height: 150,
    padding: '$3',
    mb: '$3',
    flexGrow: 1,
    borderRadius: 10,
    borderWidth: 0,
    borderColor: '$borderDivider',
    justifyContent: 'space-between',
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 5
    },
    shadowOpacity: 0.08,
    shadowRadius: 15,
    elevation: 10,
    gap: '$2'
  },
  top: {
    flex: 1,
    gap: '$1'
  },
  tenorTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: '$1'
  },
  apyTag: {
    flex: 1,
    paddingY: 2,
    paddingX: '$2',
    borderRadius: 100,
    backgroundColor: '$backgroundTheme',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '$2'
  },
  bottom: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: '$1'
  }
};
